import React, { useState, useRef, useEffect } from 'react'
import { BASE_PPI } from './SheetCanvas'

const SAFETY_MARGIN_IN = 0.125

export default function PositionPopover({ canvas, selectedObject, sheetSize, onChange }) {
  const [open, setOpen] = useState(false)
  const [keepInSafeZone, setKeepInSafeZone] = useState(false)
  const popoverRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) setOpen(false)
    }
    const handleEscape = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [open])

  useEffect(() => { setOpen(false) }, [selectedObject])

  const sheetWidthPx = sheetSize.width_in * BASE_PPI
  const sheetHeightPx = sheetSize.height_in * BASE_PPI
  const safetyPx = keepInSafeZone ? SAFETY_MARGIN_IN * BASE_PPI : 0

  const align = (position) => {
    if (!selectedObject || !canvas) return
    const bounds = selectedObject.getBoundingRect()
    const objW = bounds.width
    const objH = bounds.height
    const offsetX = selectedObject.left - bounds.left
    const offsetY = selectedObject.top - bounds.top

    const positions = {
      top:    { top: safetyPx + offsetY },
      bottom: { top: sheetHeightPx - safetyPx - objH + offsetY },
      left:   { left: safetyPx + offsetX },
      right:  { left: sheetWidthPx - safetyPx - objW + offsetX },
      middle: { top: (sheetHeightPx - objH) / 2 + offsetY },
      center: { left: (sheetWidthPx - objW) / 2 + offsetX },
    }

    canvas.fire('before:transform', { transform: { target: selectedObject } })
    selectedObject.set(positions[position])
    selectedObject.setCoords()
    canvas.fire('object:modified', { target: selectedObject })
    canvas.requestRenderAll()
    onChange?.()
  }

  const layer = (action) => {
    if (!selectedObject || !canvas) return
    const actions = {
      forward:  () => canvas.bringObjectForward(selectedObject),
      backward: () => canvas.sendObjectBackwards(selectedObject),
      front:    () => canvas.bringObjectToFront(selectedObject),
      back:     () => canvas.sendObjectToBack(selectedObject),
    }
    actions[action]?.()
    canvas.fire('object:modified', { target: selectedObject })
    canvas.requestRenderAll()
    onChange?.()
  }

  return (
    <div className="selection-toolbar__popover-anchor" ref={popoverRef}>
      <button
        className={`selection-toolbar__btn${open ? ' selection-toolbar__btn--active' : ''}`}
        onClick={() => setOpen(!open)}
        title="Position"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="5 9 2 12 5 15" />
          <polyline points="9 5 12 2 15 5" />
          <polyline points="15 19 12 22 9 19" />
          <polyline points="19 9 22 12 19 15" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <line x1="12" y1="2" x2="12" y2="22" />
        </svg>
        Position
      </button>

      {open && (
        <div className="selection-toolbar__popover selection-toolbar__popover--position">
          <div className="position-popover__section">
            <h4 className="position-popover__heading">Align to page</h4>
            <div className="position-popover__grid">
              <button className="position-popover__btn" onClick={() => align('top')}>Top</button>
              <button className="position-popover__btn" onClick={() => align('left')}>Left</button>
              <button className="position-popover__btn" onClick={() => align('bottom')}>Bottom</button>
              <button className="position-popover__btn" onClick={() => align('right')}>Right</button>
              <button className="position-popover__btn" onClick={() => align('middle')}>Middle</button>
              <button className="position-popover__btn" onClick={() => align('center')}>Center</button>
            </div>
            <label className="position-popover__checkbox">
              <input
                type="checkbox"
                checked={keepInSafeZone}
                onChange={(e) => setKeepInSafeZone(e.target.checked)}
              />
              Keep in safe zone
            </label>
          </div>

          <hr className="position-popover__divider" />

          <div className="position-popover__section">
            <h4 className="position-popover__heading">Layers</h4>
            <div className="position-popover__grid">
              <button className="position-popover__btn" onClick={() => layer('forward')}>Bring forward</button>
              <button className="position-popover__btn" onClick={() => layer('backward')}>Send backward</button>
              <button className="position-popover__btn" onClick={() => layer('front')}>Bring to front</button>
              <button className="position-popover__btn" onClick={() => layer('back')}>Send to back</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
